import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Bell, Mail, MessageSquare, Smartphone, Send, RefreshCw, CheckCircle,
  XCircle, Clock, AlertTriangle, Filter, Search, Settings, TrendingUp,
  Users, Activity, Zap, ChevronRight, Eye, MoreHorizontal
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCenter = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [notificationForm, setNotificationForm] = useState({
    recipient_email: '',
    recipient_phone: '',
    channels: ['email'],
    data: {}
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase59/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      } else {
        setDashboardData(generateSampleData());
      }
    } catch (error) {
      console.error('Error:', error);
      setDashboardData(generateSampleData());
    } finally {
      setLoading(false);
    }
  };

  const generateSampleData = () => ({
    stats: { total_sent: 156, sent_today: 23, failed: 2, pending: 5, delivery_rate: 98.7 },
    channel_stats: [
      { channel: 'sms', count: 45 },
      { channel: 'email', count: 89 },
      { channel: 'push', count: 12 },
      { channel: 'in_app', count: 10 }
    ],
    type_stats: [
      { type: 'visit_reminder', category: 'scheduling', count: 45 },
      { type: 'claim_denied', category: 'billing', count: 12 },
      { type: 'medication_refill', category: 'pharmacy', count: 28 },
      { type: 'patient_critical', category: 'clinical', count: 5 },
      { type: 'training_due', category: 'compliance', count: 15 }
    ],
    recent_notifications: [
      { id: '1', type: 'visit_reminder', category: 'scheduling', priority: 'medium', status: 'sent', recipient_email: 'nurse@example.com', channels: ['email', 'sms'], created_at: new Date().toISOString() },
      { id: '2', type: 'claim_denied', category: 'billing', priority: 'high', status: 'sent', recipient_email: 'billing@example.com', channels: ['email'], created_at: new Date(Date.now() - 3600000).toISOString() },
      { id: '3', type: 'patient_critical', category: 'clinical', priority: 'critical', status: 'sent', recipient_email: 'doctor@example.com', channels: ['email', 'sms', 'push'], created_at: new Date(Date.now() - 7200000).toISOString() }
    ],
    notification_types: ['claim_denied', 'claim_approved', 'medication_refill', 'visit_reminder', 'patient_critical', 'patient_decline', 'dme_delivery', 'training_due', 'shift_reminder', 'incident_reported']
  });

  const sendNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase59/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: selectedType,
          recipient_id: 'user_001',
          recipient_email: notificationForm.recipient_email,
          recipient_phone: notificationForm.recipient_phone,
          channels: notificationForm.channels,
          data: {
            patient_name: 'John Smith',
            claim_id: 'CLM-12345',
            amount: '1500.00',
            reason: 'Missing documentation',
            medication_name: 'Morphine Sulfate',
            refills_remaining: '2',
            visit_date: new Date().toLocaleDateString(),
            visit_time: '10:00 AM',
            location: '123 Care St',
            visit_type: 'Routine',
            ...notificationForm.data
          }
        })
      });

      if (response.ok) {
        toast.success('Notification sent successfully');
        setShowSendDialog(false);
        fetchDashboard();
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error) {
      toast.error('Error sending notification');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      delivered: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      mocked: { color: 'bg-purple-100 text-purple-800', icon: Zap }
    };
    const cfg = config[status] || config.pending;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1 font-semibold`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      critical: 'bg-red-600 text-white font-bold',
      high: 'bg-orange-500 text-white font-semibold',
      medium: 'bg-yellow-500 text-black font-semibold',
      low: 'bg-gray-400 text-white font-semibold'
    };
    return <Badge className={colors[priority] || colors.medium}>{priority.toUpperCase()}</Badge>;
  };

  const getChannelIcon = (channel) => {
    const icons = {
      sms: <Smartphone className="h-4 w-4" />,
      email: <Mail className="h-4 w-4" />,
      push: <Bell className="h-4 w-4" />,
      in_app: <MessageSquare className="h-4 w-4" />
    };
    return icons[channel] || <Bell className="h-4 w-4" />;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notification-center">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Notification Center</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">Real-time SMS, Email & Push notifications</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowSendDialog(true)}>
                <Send className="h-4 w-4 mr-2" />
                Send Notification
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="history" className="font-semibold">History</TabsTrigger>
            <TabsTrigger value="settings" className="font-semibold">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Total Sent" value={dashboardData?.stats?.total_sent || 0} icon={Send} color="blue" />
              <StatCard title="Sent Today" value={dashboardData?.stats?.sent_today || 0} icon={TrendingUp} color="green" />
              <StatCard title="Pending" value={dashboardData?.stats?.pending || 0} icon={Clock} color="yellow" />
              <StatCard title="Failed" value={dashboardData?.stats?.failed || 0} icon={XCircle} color="red" />
              <StatCard title="Delivery Rate" value={`${dashboardData?.stats?.delivery_rate || 0}%`} icon={CheckCircle} color="green" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Channel Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">By Channel</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dashboardData?.channel_stats?.map((channel) => (
                      <div key={channel.channel} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            {getChannelIcon(channel.channel)}
                          </div>
                          <span className="font-semibold capitalize text-slate-900">{channel.channel}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-teal-600 h-2 rounded-full"
                              style={{ width: `${(channel.count / (dashboardData?.stats?.total_sent || 1)) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-slate-900 w-12 text-right">{channel.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Type Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-bold">By Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.type_stats?.slice(0, 6).map((type) => (
                      <div key={type.type} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div>
                          <span className="font-semibold capitalize text-slate-900">{type.type.replace(/_/g, ' ')}</span>
                          <span className="text-xs text-slate-600 ml-2 font-medium">({type.category})</span>
                        </div>
                        <Badge variant="outline" className="font-bold">{type.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Recent Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Type</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Recipient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Channels</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Priority</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_notifications?.map((notification) => (
                        <tr key={notification.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-semibold capitalize text-slate-900">{notification.type.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-600 block font-medium">{notification.category}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 font-medium">{notification.recipient_email}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {notification.channels?.map((ch) => (
                                <span key={ch} className="p-1 bg-slate-100 rounded" title={ch}>
                                  {getChannelIcon(ch)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">{getPriorityBadge(notification.priority)}</td>
                          <td className="px-4 py-3">{getStatusBadge(notification.status)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 font-medium">
                            {new Date(notification.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-slate-900 font-bold">Notification History</CardTitle>
                  <div className="flex gap-2">
                    <Input placeholder="Search..." className="w-48" />
                    <Button variant="outline"><Filter className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData?.recent_notifications?.map((notification) => (
                    <div key={notification.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${notification.priority === 'critical' ? 'bg-red-100' : 'bg-teal-100'}`}>
                          <Bell className={`h-5 w-5 ${notification.priority === 'critical' ? 'text-red-600' : 'text-teal-600'}`} />
                        </div>
                        <div>
                          <p className="font-semibold capitalize text-slate-900">{notification.type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-slate-600 font-medium">{notification.recipient_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(notification.status)}
                        <span className="text-sm text-slate-600 font-medium">{new Date(notification.created_at).toLocaleString()}</span>
                        <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Notification Preferences</CardTitle>
                <CardDescription className="text-slate-600 font-medium">Configure default notification channels and behaviors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-slate-900">SMS Notifications</Label>
                    <p className="text-sm text-slate-600 font-medium">Send notifications via SMS (Twilio)</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-slate-900">Email Notifications</Label>
                    <p className="text-sm text-slate-600 font-medium">Send notifications via Email (Resend)</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-slate-900">Push Notifications</Label>
                    <p className="text-sm text-slate-600 font-medium">Send browser push notifications</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-slate-900">In-App Notifications</Label>
                    <p className="text-sm text-slate-600 font-medium">Show notifications within the app</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="pt-4 border-t">
                  <Label className="font-semibold text-slate-900">Quiet Hours</Label>
                  <p className="text-sm text-slate-600 mb-3 font-medium">Non-critical notifications will be held during these hours</p>
                  <div className="flex gap-4">
                    <div>
                      <Label className="text-sm text-slate-700 font-medium">Start</Label>
                      <Input type="time" defaultValue="22:00" className="w-32" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-700 font-medium">End</Label>
                      <Input type="time" defaultValue="07:00" className="w-32" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Notification Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">Send Notification</DialogTitle>
            <DialogDescription className="text-slate-600 font-medium">Send a notification via SMS, Email, or Push</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-semibold text-slate-900">Notification Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {dashboardData?.notification_types?.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Recipient Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={notificationForm.recipient_email}
                onChange={(e) => setNotificationForm({ ...notificationForm, recipient_email: e.target.value })}
              />
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Recipient Phone (SMS)</Label>
              <Input
                type="tel"
                placeholder="+1234567890"
                value={notificationForm.recipient_phone}
                onChange={(e) => setNotificationForm({ ...notificationForm, recipient_phone: e.target.value })}
              />
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Channels</Label>
              <div className="flex gap-2 mt-2">
                {['email', 'sms', 'push', 'in_app'].map((channel) => (
                  <Button
                    key={channel}
                    variant={notificationForm.channels.includes(channel) ? 'default' : 'outline'}
                    size="sm"
                    className={notificationForm.channels.includes(channel) ? 'bg-teal-600 text-white' : ''}
                    onClick={() => {
                      const channels = notificationForm.channels.includes(channel)
                        ? notificationForm.channels.filter((c) => c !== channel)
                        : [...notificationForm.channels, channel];
                      setNotificationForm({ ...notificationForm, channels });
                    }}
                  >
                    {getChannelIcon(channel)}
                    <span className="ml-1 capitalize">{channel.replace('_', ' ')}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={sendNotification} disabled={!selectedType}>
              <Send className="h-4 w-4 mr-2" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationCenter;
